<template>
  <v-card
    :height="widgetHeight"
    :min-width="widgetWidth"
    class="ma-0 pa-0"
    :loading="loading"
  >
    <v-card-title v-if="!loading" class="pa-0 ma-0">
      <widget-header :title="title" color="indigo" icon="mdi-chart-pie">
        <template slot="actions">
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="refresh"
                >mdi-refresh</v-icon
              >
            </template>
            <span>{{ $t("Refresh") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="removeWidget"
                >mdi-delete</v-icon
              >
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </template>
      </widget-header>
    </v-card-title>
    <v-card-text class="pa-0 ma-0">
      <div
        v-if="!loading"
        :style="{
          'max-height': contentHeight + 'px',
          'max-width': contentWidth + 'px',
        }"
        class="overflow-y-auto ma-1 py-1"
      >
        <base-no-data v-if="!hasData && widgetHeight > 39" />
        <apexchart
          type="pie"
          :options="chartOptions"
          :series="series"
          v-if="hasData"
        ></apexchart>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import WidgetService from '@/services/WidgetService';
import WidgetHeader from '@/components/widgets/WidgetHeader';
import WidgetType from '@/ecm/variables/WidgetType';
import VueApexCharts from 'vue-apexcharts';
import { mapGetters } from 'vuex';
import { EventBus } from '@/eventBus';

export default {
    components: { WidgetHeader, VueApexCharts, apexchart: VueApexCharts },
    name: 'WidgetPieChart',
    props: {
        widgetId: {
            type: Number,
            default: 0,
        },
        enableEdit: {
            type: Boolean,
            default: false,
        },
        id: {
            type: Number,
            default: 0,
        },
        height: { type: Number, default: 0 },
        width: { type: Number, default: 0 },
    },
    mounted() {
        this.widgetHeight = this.height;
        this.widgetWidth = this.width;
        this.refresh();

        this.connection = $.hubConnection(this.apiUrl);
        this.connection.qs = { access_token: this.token };
        this.notificationHub = this.connection.createHubProxy('notificationHub');
        const self = this;

        this.onRefresh = (notifications) => {
            const now = Date.now();
            const timeSinceLastMessage = now - self.lastMessageTime;

            // Clear any existing timeout
            if (self.refreshTimeout) {
                clearTimeout(self.refreshTimeout);
                self.refreshTimeout = null;
            }

            // Determine if messages are coming rapidly
            if (timeSinceLastMessage < self.rapidThreshold && self.lastMessageTime > 0) {
                // Messages are rapid - increase the delay
                self.messageCount++;
            } else {
                // Messages have slowed down or this is the first message
                self.messageCount = 1;
            }

            self.lastMessageTime = now;

            // Calculate adaptive delay based on message frequency
            // The more messages in quick succession, the longer we wait
            let delay = self.baseDelay;
            if (self.messageCount > 1) {
                // Exponential backoff: 1s, 2s, 4s, 8s, up to maxDelay
                delay = Math.min(
                    self.baseDelay * Math.pow(2, self.messageCount - 1),
                    self.maxDelay
                );
            }

            // Schedule the refresh
            self.refreshTimeout = setTimeout(() => {
                self.refresh();
                // Reset the message count after refresh
                self.messageCount = 0;
                self.refreshTimeout = null;
            }, delay);
        };

        this.notificationHub.off('refresh', this.onRefresh);
        this.notificationHub.on('refresh', this.onRefresh);

        // Handle SignalR events for connection status
        this.onReconnected = () => { self.refresh(); };
        this.onDisconnected = () => { self.tryReconnect(); };
        this.onError = (ex) => { console.error('SignalR error:', ex); };

        this.connection.reconnected(this.onReconnected);
        this.connection.disconnected(this.onDisconnected);
        this.connection.error(this.onError);

        this.connection.start();
    },
    beforeDestroy() {
        // Clear any pending refresh timeout
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }

        if (this.notificationHub && this.connection) {
            if (this.onRefresh) this.notificationHub.off('refresh');

            const $c = $(this.connection);

            $c.off('onReconnect');
            $c.off('onDisconnect');
            $c.off('onError');

            this.connection.stop();
            this.connection = null;
        }
    },
    data() {
        return {
            // SignalR
            connection: null,
            notificationHub: null,
            onRefresh: null,
            onReconnecting: null,
            onDisconnected: null,
            onError: null,
            signalRIsTryingToReconnect: false,
            // Adaptive refresh control
            refreshTimeout: null,
            messageCount: 0,
            lastMessageTime: 0,
            baseDelay: 1000, // Base delay: 1 second
            maxDelay: 10000, // Maximum delay: 10 seconds
            rapidThreshold: 500, // If messages come within 500ms, consider it rapid

            title: '',
            loading: false,
            hasData: false,
            headerHeight: 66,
            widgetHeight: 0,
            widgetWidth: 0,
            chartOptions: {
                legend: {
                    show: true,
                    position: 'bottom',
                },
                labels: [],
            },
            series: [],
        };
    },
    methods: {
        resize(height, width) {
            this.widgetHeight = height;
            this.widgetWidth = width;
        },
        refresh() {
            this.loading = true;

            WidgetService.getData({
                WidgetID: this.widgetId,
            }).then((widgetData) => {
                if (widgetData.length == 0) {
                    this.hasData = false;
                    this.loading = false;
                    return;
                }

                if (this.widgetId == WidgetType.DocumentsByContentTypeChart) {
                    this.title =
                        this.$t('WidgetDocumentsByContentTypeTitle') +
                        ': ' +
                        widgetData[0].Total;
                }
                if (this.widgetId == WidgetType.DocumentsByWorkflowChart) {
                    this.title =
                        this.$t('WidgetDocumentsByWorkflowTitle') +
                        ': ' +
                        widgetData[0].Total;
                }

                const series = [];
                const labels = [];

                widgetData.forEach((element) => {
                    series.push(element.SeriesValueY_AsDecimal);
                    labels.push(element.SeriesValueX_AsString);
                });

                this.chartOptions.labels = labels;
                this.series = series;

                this.hasData = true;
                this.loading = false;
            });
        },
        removeWidget() {
            this.$emit('on-remove-widget', this.id);
        },
        tryReconnect() {
            if (this.signalRIsTryingToReconnect)
                return;

            let attempts = 0;
            const maxAttempts = 5;
            const self = this;
            this.signalRIsTryingToReconnect = true;

            function reconnect() {
                if (attempts < maxAttempts) {
                    self.connection.start().done(() => {
                        self.signalRIsTryingToReconnect = false;
                        EventBus.$emit('connection-status', null);
                    }).fail(() => {
                        attempts++;
                        setTimeout(reconnect, 5000); // Retry after 5 seconds
                    });
                } else {
                    console.log('Max reconnection attempts reached. Could not reconnect to SignalR.');
                }
            }

            reconnect();
        }
    },
    computed: {
        ...mapGetters({
            user: 'account/user',
            apiUrl: 'account/apiUrl',
            token: 'account/token',
            userContentTypes: 'account/userContentTypes',
        }),
        contentHeight() {
            return this.widgetHeight - this.headerHeight;
        },
        contentWidth() {
            return this.widgetWidth - 20;
        },
    },
};
</script>
